import React, { useRef, Suspense, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react'; 
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sphere, Stars, Text, Box, Preload } from '@react-three/drei';

// Central Animated Globe
const AnimatedGlobe = () => {
  const sphereRef = useRef();

  useFrame(({ clock }) => {
    sphereRef.current.rotation.x = clock.getElapsedTime() * 0.2;
    sphereRef.current.rotation.y = clock.getElapsedTime() * 0.3;
  });

  return (
    <Sphere visible args={[1, 32, 32]} scale={2} ref={sphereRef}> {/* Reduced segments for cleaner wireframe */}
      <meshStandardMaterial 
          color="#960000" // Updated to Primary Red
          metalness={0.6}
          roughness={0.2} 
          wireframe // Added wireframe to simulate a globe structure
        />
    </Sphere>
  );
};

// Orbiting Service Item with Gold Card and Text
const ServiceItem = ({ position, label, delay }) => { 
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  // Random floating movement
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() + delay;
    meshRef.current.position.y = position[1] + Math.sin(t) * 0.2;
    meshRef.current.rotation.x = Math.sin(t * 0.5) * 0.1;
    meshRef.current.rotation.y = Math.cos(t * 0.3) * 0.1;
  });

  // Handle cursor change on hover
  useEffect(() => {
    // Only change cursor if interaction was intended, but since click is disabled,
    // we might want to remove this or keep it just for visual feedback.
    // I will remove pointer cursor to signify it's not clickable.
    document.body.style.cursor = 'auto'; 
    return () => { document.body.style.cursor = 'auto'; };
  }, [hovered]);

  return (
    <group 
      position={position} 
      ref={meshRef}
      // onClick handler removed to disable click
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {/* The Gold Card */}
      <Box args={[2.8, 1.2, 0.1]} scale={hovered ? 1.1 : 1}> 
        <meshStandardMaterial 
          color="#D4AF37" // Primary Gold
          metalness={0.6}
          roughness={0.2} 
        />
      </Box>
      
      {/* 3D Text Label - Front Side */}
      <Text
        position={[0, 0, 0.06]} 
        fontSize={0.22}
        color="#FFFFFF" // White text
        anchorX="center"
        anchorY="middle"
        fontWeight="bold"
      >
        {label}
      </Text>

      {/* 3D Text Label - Back Side (Rotated 180 degrees) */}
      <Text
        position={[0, 0, -0.06]} 
        rotation={[0, Math.PI, 0]} // Rotate 180 degrees around Y axis
        fontSize={0.22}
        color="#FFFFFF" // White text
        anchorX="center"
        anchorY="middle"
        fontWeight="bold"
      >
        {label}
      </Text>
    </group>
  );
};

// Container for the orbiting services
const ServiceRing = ({ navigate }) => {
  const groupRef = useRef();

  useFrame(({ clock }) => {
    // Rotate the entire ring around the center
    groupRef.current.rotation.y = clock.getElapsedTime() * 0.15;
  });

  // Updated Labels without Images
  const services = [
    { label: "Integrated\nSynergy", pos: [4, 0, 0] },
    { label: "Result-\nOriented", pos: [0, 0, 4] },
    { label: "Human-\nCentric", pos: [-4, 0, 0] },
    { label: "Strategic\nInnovation", pos: [0, 0, -4] }
  ];

  return (
    <group ref={groupRef} rotation={[0.2, 0, 0]}> {/* Slight tilt to the ring */}
      {services.map((s, i) => (
        <ServiceItem 
          key={i} 
          position={s.pos} 
          label={s.label} 
          delay={i * 2}
          // navigate prop not passed since it's not used
        />
      ))}
    </group>
  );
};

// --- NEW ANIMATED COMPONENT: Connected Capabilities ---
const ConnectedCapabilities = () => {
  const arcVariants = {
    animate: {
      rotate: [-5, 5],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    animate: {
      rotate: [5, -5],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }
    }
  };

  const dotVariants = {
    animate: {
      scale: [1, 1.2, 1],
      boxShadow: [
        "0 0 0 0px rgba(150, 0, 0, 0.7)", 
        "0 0 0 6px rgba(150, 0, 0, 0)", 
        "0 0 0 0px rgba(150, 0, 0, 0)"
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  return (
    <motion.section 
      className="capabilities-section"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <div className="container" style={{ position: 'relative', height: '100%' }}>
        
        <Link to="/portfolio" className="capabilities-badge-link" onClick={scrollToTop}>
          <div className="capabilities-badge">
            <span>Connected<br/>Capabilities</span>
            <div className="badge-arrow">
              <ArrowUpRight size={20} />
            </div>
          </div>
        </Link>

        <div className="arc-display">
          <div className="arc-curve"></div>
          
          <motion.div 
            className="nodes-wrapper"
            variants={arcVariants}
            animate="animate"
            style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, transformOrigin: '50% 0%' }}
          >
            {/* Node 1 - Web Dev */}
            <motion.div variants={itemVariants} className="cap-node node-1">
              <Link to="/portfolio" state={{ activeTab: 'Web Dev' }} style={{display: 'contents'}} onClick={scrollToTop}>
                <motion.div variants={dotVariants} className="node-dot"></motion.div>
                <span className="node-text">Website<br/>Development</span>
              </Link>
            </motion.div>

            {/* Node 2 - Visual Storytelling */}
            <motion.div variants={itemVariants} className="cap-node node-2">
              <Link to="/portfolio" state={{ activeTab: 'Video Editing' }} style={{display: 'contents'}} onClick={scrollToTop}>
                <motion.div variants={dotVariants} className="node-dot"></motion.div>
                <span className="node-text">Visual<br/>Storytelling</span>
              </Link>
            </motion.div>

            {/* Node 3 - Branding (NEW CENTRAL NODE) */}
            <motion.div variants={itemVariants} className="cap-node node-3">
              <Link to="/portfolio" state={{ activeTab: 'Branding' }} style={{display: 'contents'}} onClick={scrollToTop}>
                <motion.div variants={dotVariants} className="node-dot"></motion.div>
                <span className="node-text">Branding<br/>Strategy</span>
              </Link>
            </motion.div>

            {/* Node 4 - Social Media */}
            <motion.div variants={itemVariants} className="cap-node node-4">
              <Link to="/portfolio" state={{ activeTab: 'Social Media' }} style={{display: 'contents'}} onClick={scrollToTop}>
                <motion.div variants={dotVariants} className="node-dot"></motion.div>
                <span className="node-text">Social Media<br/>Management</span>
              </Link>
            </motion.div>

            {/* Node 5 - Graphic Design */}
            <motion.div variants={itemVariants} className="cap-node node-5">
              <Link to="/portfolio" state={{ activeTab: 'Graphic Design' }} style={{display: 'contents'}} onClick={scrollToTop}>
                <motion.div variants={dotVariants} className="node-dot"></motion.div>
                <span className="node-text">Graphic<br/>Design</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>

      </div>
    </motion.section>
  );
};

const Home = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section with Video Background */}
      <header className="hero">
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <video 
            src="/Image To Video Generation.mp4" 
            autoPlay 
            loop 
            muted 
            playsInline 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        
        <div className="hero-overlay" style={{ background: 'rgba(20, 20, 20, 0.7)' }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Where Vision Meets <span className="text-gold">Value</span>
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Your integrated partner in digital excellence.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}
          >
            <Link to="/services" className="btn btn-primary">Our Services</Link>
            <Link to="/contact" className="btn btn-secondary">Get in Touch</Link>
          </motion.div>
        </div>
      </header>

      <ConnectedCapabilities />

      {/* Mission Summary - Animated on Scroll */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <motion.div
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6 }}
          >
            <h2>The <span style={{ color: 'var(--primary-gold)' }}>Valu<span style={{ color: 'var(--primary-red)' }}>Vant</span></span> Difference</h2>
            <p style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>
              We don’t just deliver projects; we build the infrastructure for your long-term digital success.
            </p>
          </motion.div>

          {/* 3D Scene Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{
              maxWidth: '900px',
              height: '500px', /* Fixed height needed for Canvas to render */
              margin: '0 auto 4rem',
              background: '#111',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div style={{ width: '100%', height: '100%' }}>
              <Canvas camera={{ position: [0, 0, 9], fov: 45 }}>
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} intensity={1} />
                
                <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
                
                <Suspense fallback={null}>
                  <group position={[0, 0, 0]}>
                    <AnimatedGlobe />
                    <ServiceRing navigate={navigate} />
                  </group>
                  <Preload all />
                </Suspense>

                {/* DISABLED user interaction (rotate, pan, zoom) but KEPT autoRotate */}
                <OrbitControls 
                  enableZoom={false} 
                  enablePan={false} 
                  enableRotate={false} 
                  autoRotate 
                  autoRotateSpeed={0.5} 
                />
              </Canvas>
            </div>
          </motion.div>
          
          <div className="services-grid">
            {[
              { title: "Integrated Synergy", desc: "We handle everything from code to captions. No more \"lost in translation\" moments between freelancers." },
              { title: "Result-Oriented", desc: "We look past vanity metrics to focus on ROI and what actually moves the needle for your business." },
              { title: "Human-Centric", desc: "We design with empathy, ensuring your brand connects with real human emotions and needs." },
              { title: "Strategic Innovation", desc: "We leverage cutting-edge technology and creative strategies to keep your brand ahead of the curve." }
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }} 
                className="service-card"
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW SECTION: Detailed Services List */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-red" style={{ textAlign: 'center', marginBottom: '2rem' }}>Comprehensive Service Suite</h2>
            <div className="detailed-list-grid">
              <ul>
                <li>Website creation & Management</li>
                <li>Brand Strategy & Market Positioning</li>
                <li>Brand Naming, Logos & Taglines</li>
                <li>Brand Architecture & Identity Systems</li>
                <li>Public Relations Strategy & Reputation Building</li>
              </ul>
              <ul>
                <li>Content Creation & Brand Storytelling</li>
                <li>Media Relations & Influencer Partnerships</li>
                <li>Social Media Management & Digital PR</li>
                <li>Search Visibility, SEO & Paid Advertising (SEM/PPC)</li>
                <li>Email Marketing, Campaigns & Automation</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Animated on Scroll */}
      <section className="section bg-light">
        <motion.div 
          className="container" 
          style={{ textAlign: 'center' }}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-red">Ready to Scale?</h2>
          <p>Whether you are a startup or an established enterprise, we are ready to build the future with you.</p>
          <br />
          <Link to="/contact" className="btn btn-primary">Start Your Project <ArrowRight size={16} style={{marginLeft: '5px'}}/></Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default Home;